// @react-lib/hooks/useTheme.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { themeStore, type ThemeMode, type ResolvedTheme, type ThemeColors, type ThemeConfig } from '@core/stores';

/**
 * Hook for theme management
 * Provides access to theme store and theme control functions
 */
export function useTheme() {
  const store = themeStore;

  /**
   * Set the theme mode
   */
  const setMode = useCallback(
    (mode: ThemeMode): void => {
      store.setMode(mode);
    },
    [store]
  );

  /**
   * Toggle between light and dark themes
   */
  const toggle = useCallback((): void => {
    store.toggle();
  }, [store]);

  /**
   * Update custom colors
   */
  const setColors = useCallback(
    (config: ThemeConfig): void => {
      store.setColors(config);
    },
    [store]
  );

  /**
   * Reset to default colors
   */
  const resetColors = useCallback((): void => {
    store.resetColors();
  }, [store]);

  return {
    // Current theme mode (light, dark, system)
    mode: store.mode,
    // Resolved theme (light or dark)
    resolvedTheme: store.resolvedTheme,
    // Is dark mode active
    isDark: store.isDark,
    // Current theme colors
    colors: store.colors,
    // Initialization state
    isInitialized: store.isInitialized,
    // Set theme mode
    setMode,
    // Toggle theme
    toggle,
    // Set custom colors
    setColors,
    // Reset colors to default
    resetColors,
  };
}

/**
 * HOC to make a component observe theme changes
 */
export function withTheme<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return observer(Component as React.FunctionComponent<P>) as unknown as React.ComponentType<P>;
}

export type { ThemeMode, ResolvedTheme, ThemeColors, ThemeConfig };
